import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import { uploadFileToCampusFileSystem, getFileById } from '../../services/CampusFileSystemService';
import { 
    MailOutline as MailIcon, 
    Send as SendIcon, 
    Inbox as InboxIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    AccountCircle as UserIcon,
    ChatBubbleOutline as ChatIcon,
    Close as CloseIcon,
    AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { Edit as EditIcon } from '@mui/icons-material';
import './StudentInbox.css';

const StudentInbox = () => {
    const { user, token, axios: authAxios, orgId, studentInfo, refreshUnreadCount } = useAuth();
    const resolvedOrgId = user?.orgId || studentInfo?.orgId || orgId || "";
    const currentUserId = user?.userId || studentInfo?.userId || user?.id || studentInfo?.id || "";

    const stripHtml = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };
    const [mailboxItems, setMailboxItems] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [selectedConversationMessages, setSelectedConversationMessages] = useState([]);
    const [isLoadingMailbox, setIsLoadingMailbox] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [replyBody, setReplyBody] = useState('');
    const [isSendingReply, setIsSendingReply] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const threadEndRef = useRef(null);

    const handleDownloadInboxAttachment = async (att) => {
        const displayName = att.filename || 'Document';
        const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
        const uuidMatch = (att.url || '').match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
        const fileId = att.fileId || (uuidMatch ? uuidMatch[0] : null);

        try {
            toast.info(`Downloading ${displayName}...`);
            let downloadUrl = att.url;

            if (fileId) {
                const freshDoc = await getFileById(fileId, rawToken, true);
                downloadUrl = freshDoc?.downloadUrl || freshDoc?.documentUrl || freshDoc?.url || downloadUrl;
            }

            if (!downloadUrl) {
                toast.error("Download link is not available.");
                return;
            }

            const response = await fetch(downloadUrl);
            if (!response.ok) {
                if (fileId) {
                    const retryDoc = await getFileById(fileId, rawToken, true);
                    const retryUrl = retryDoc?.downloadUrl;
                    if (retryUrl) {
                        const retryRes = await fetch(retryUrl);
                        if (retryRes.ok) {
                            const blob = await retryRes.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.download = displayName;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);
                            toast.success(`Downloaded ${displayName}`);
                            return;
                        }
                    }
                }
                throw new Error("Download request expired or file inaccessible. Please try again.");
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = displayName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            toast.success(`Downloaded ${displayName}`);
        } catch (err) {
            console.error("Failed to download attachment:", err);
            toast.error(err.message || "Could not download attachment.");
        }
    };

    const renderMessageBody = (rawBody) => {
        if (!rawBody) return '(Empty message)';

        let isHtml = /<[a-z][\s\S]*>/i.test(rawBody) || /&lt;[a-z]/i.test(rawBody);
        if (isHtml) {
            let htmlContent = String(rawBody);
            if (htmlContent.includes('&lt;') && htmlContent.includes('&gt;')) {
                try {
                    const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
                    htmlContent = doc.body.textContent || doc.body.innerText || htmlContent;
                } catch (e) {
                    htmlContent = htmlContent.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
                }
            }
            return (
                <div 
                    className="inbox-rendered-html-body"
                    style={{ lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            );
        }

        const attachments = [];
        const paymentLinks = [];
        const markdownLinkRegex = /\[([^\]]+)\]\(((?:https?|upi|intent):\/\/[^\s\)]+)\)/gi;

        let match;
        while ((match = markdownLinkRegex.exec(rawBody)) !== null) {
            const label = match[1];
            const url = match[2];
            if (label.toLowerCase().includes('qr') || label.toLowerCase().includes('document') || url.includes('qrserver') || url.includes('.pdf') || url.includes('campus-file-system')) {
                attachments.push({ filename: label, url });
            } else {
                paymentLinks.push({ label, url });
            }
        }

        let cleanText = rawBody
            .replace(/Attachments:\s*(?:📎\s*(?:Document:)?\s*\[[^\]]+\]\((?:https?|upi|intent):\/\/[^\s\)]+\)\s*)*/gi, '')
            .trim();

        const parts = [];
        const fullRegex = /\[([^\]]+)\]\(((?:https?|upi|intent):\/\/[^\s\)]+)\)|((?:https?|upi|intent):\/\/[^\s\)]+)/gi;
        let lastIndex = 0;
        let m;

        while ((m = fullRegex.exec(cleanText)) !== null) {
            if (m.index > lastIndex) {
                parts.push(cleanText.substring(lastIndex, m.index));
            }
            if (m[1] && m[2]) {
                const linkText = m[1];
                const linkUrl = m[2];
                const isPayment = linkUrl.startsWith('upi:') || linkText.toLowerCase().includes('pay') || linkUrl.includes('pay');
                parts.push(
                    <a
                        key={m.index}
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: isPayment ? '#059669' : '#014486',
                            fontWeight: 700,
                            textDecoration: 'underline',
                            wordBreak: 'break-all',
                            margin: '0 2px'
                        }}
                    >
                        {linkText}
                    </a>
                );
            } else if (m[3]) {
                const rawUrl = m[3];
                const isPayment = rawUrl.startsWith('upi:') || rawUrl.includes('pay');
                parts.push(
                    <a
                        key={m.index}
                        href={rawUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: isPayment ? '#059669' : '#014486',
                            fontWeight: 700,
                            textDecoration: 'underline',
                            wordBreak: 'break-all',
                            margin: '0 2px'
                        }}
                    >
                        {rawUrl}
                    </a>
                );
            }
            lastIndex = m.index + m[0].length;
        }
        if (lastIndex < cleanText.length) {
            parts.push(cleanText.substring(lastIndex));
        }

        return (
            <div>
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', fontSize: '14px', color: '#0f172a' }}>
                    {parts.length > 0 ? parts : stripHtml(cleanText)}
                </div>

                {paymentLinks.length > 0 && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {paymentLinks.map((pl, idx) => (
                            <a
                                key={idx}
                                href={pl.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    background: '#10b981',
                                    color: '#ffffff',
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    textDecoration: 'none',
                                    boxShadow: '0 4px 10px rgba(16, 185, 129, 0.25)',
                                    width: 'fit-content'
                                }}
                            >
                                💳 {pl.label || 'Click to Pay via UPI'}
                            </a>
                        ))}
                    </div>
                )}

                {attachments.length > 0 && (
                    <div className="message-attachments-container" style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            📎 Attached Documents & Payment QR ({attachments.length})
                        </div>
                        {attachments.map((att, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                            >
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                        {att.filename}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDownloadInboxAttachment(att)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        background: '#014486',
                                        color: '#ffffff',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        border: 'none',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Download / View
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Compose & Reply Attachment State
    const [isComposeOpen, setIsComposeOpen] = useState(false);
    const [composeSubject, setComposeSubject] = useState('');
    const [composeBody, setComposeBody] = useState('');
    const [composeRecipient, setComposeRecipient] = useState(null);
    const [facultyAdminOptions, setFacultyAdminOptions] = useState([]);
    const [isSendingNewMessage, setIsSendingNewMessage] = useState(false);

    const [composeAttachments, setComposeAttachments] = useState([]);
    const [replyAttachments, setReplyAttachments] = useState([]);
    const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
    const composeAttachmentRef = useRef(null);
    const replyAttachmentRef = useRef(null);

    const handleAttachmentSelect = async (e, mode = 'compose') => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingAttachment(true);
        const tempId = `temp-${Date.now()}`;
        const tempRecord = { id: tempId, filename: file.name, size: file.size, status: 'UPLOADING' };
        const setAttachments = mode === 'compose' ? setComposeAttachments : setReplyAttachments;

        setAttachments(prev => [...prev, tempRecord]);

        try {
            const rawToken = token && token.startsWith("Bearer ") ? token.substring(7) : token;
            const record = await uploadFileToCampusFileSystem(file, {
                entityType: 'USER',
                entityId: currentUserId || 'student',
                orgId: resolvedOrgId || 'DEFAULT',
                category: 'OTHER',
                uploadedBy: currentUserId || 'student',
                token: rawToken
            });

            toast.success(`Attached document: ${file.name}`);
            setAttachments(prev => prev.map(f => f.id === tempId ? {
                id: record.fileId || tempId,
                fileId: record.fileId,
                filename: file.name,
                downloadUrl: record.downloadUrl,
                size: file.size,
                status: 'ACTIVE'
            } : f));
        } catch (err) {
            console.error("Attachment upload failed:", err);
            toast.error(err.message || "Failed to attach document.");
            setAttachments(prev => prev.filter(f => f.id !== tempId));
        } finally {
            setIsUploadingAttachment(false);
            if (e.target) e.target.value = '';
        }
    };

    const removeAttachment = (fileId, mode = 'compose') => {
        const setAttachments = mode === 'compose' ? setComposeAttachments : setReplyAttachments;
        setAttachments(prev => prev.filter(f => (f.fileId || f.id) !== fileId));
    };

    useEffect(() => {
        if (isComposeOpen && facultyAdminOptions.length === 0) {
            const fetchRecipients = async () => {
                try {
                    const [facultyRes, adminRes] = await Promise.all([
                        authAxios.get(`${configData.GET_USERS_BY_TYPE}/${resolvedOrgId}`, { params: { userType: 'FACULTY', page: 1, pageSize: 500 } }),
                        authAxios.get(`${configData.GET_USERS_BY_TYPE}/${resolvedOrgId}`, { params: { userType: 'ADMIN', page: 1, pageSize: 500 } }),
                    ]);
                    const toOption = (u, roleLabel) => ({
                        value: u.user?.userId || u.userId,
                        label: `${u.user?.firstName || u.firstName} ${u.user?.lastName || u.lastName} (${roleLabel})`,
                        type: 'INDIVIDUAL'
                    });
                    const getItems = (res) => res.data?.items || res.data?.content || [];
                    const options = [
                        ...getItems(facultyRes).map(u => toOption(u, 'Faculty')),
                        ...getItems(adminRes).map(u => toOption(u, 'Admin')),
                    ];
                    setFacultyAdminOptions(options);
                } catch (err) {
                    console.error("Error fetching faculty/admin", err);
                    toast.error("Failed to load recipients");
                }
            };
            fetchRecipients();
        }
    }, [isComposeOpen, facultyAdminOptions.length, resolvedOrgId, authAxios]);

    const handleSendNewMessage = async () => {
        if (!composeSubject.trim() || !composeBody.trim() || !composeRecipient) {
            toast.error("Please fill in all fields.");
            return;
        }

        setIsSendingNewMessage(true);
        try {
            let finalBody = composeBody.trim();
            const activeAtts = composeAttachments.filter(a => a.status === 'ACTIVE' && a.downloadUrl);
            if (activeAtts.length > 0) {
                const links = activeAtts.map(a => `📎 Document: [${a.filename}](${a.downloadUrl})`).join('\n');
                finalBody += `\n\nAttachments:\n${links}`;
            }
            const attachmentUrls = activeAtts.map(a => a.downloadUrl);

            await authAxios.post(configData.INBOX_SEND, {
                subject: composeSubject.trim(),
                body: finalBody,
                conversationId: null,
                orgId: resolvedOrgId,
                targetType: 'INDIVIDUAL',
                targetId: composeRecipient.value,
                attachmentUrls: attachmentUrls.length > 0 ? attachmentUrls : undefined
            }, {
                params: { userId: currentUserId }
            });

            toast.success("Message sent successfully!");
            setIsComposeOpen(false);
            setComposeSubject('');
            setComposeBody('');
            setComposeRecipient(null);
            setComposeAttachments([]);
            fetchMailbox();
        } catch (err) {
            console.error("Error sending message", err);
            toast.error("Failed to send message");
        } finally {
            setIsSendingNewMessage(false);
        }
    };

    const fetchMailbox = useCallback(async () => {
        if (!currentUserId || !token) return;
        setIsLoadingMailbox(true);
        try {
            const res = await authAxios.get(configData.INBOX_MAILBOX, {
                params: { 
                    userId: String(currentUserId), 
                    folder: 'INBOX', 
                    size: 50, 
                    orgId: String(resolvedOrgId) 
                }
            });
            const rawItems = res.data.items || [];
            
            // Group by conversationId to handle backend duplicates
            const groupedMap = new Map();
            rawItems.forEach(item => {
                const existing = groupedMap.get(item.conversationId);
                if (!existing || new Date(item.lastMessageAt) > new Date(existing.lastMessageAt)) {
                    groupedMap.set(item.conversationId, {
                        ...item,
                        unreadCount: (existing?.unreadCount || 0) + (item.unreadCount || 0),
                        allMailboxIds: [...(existing?.allMailboxIds || []), item.userMailboxId]
                    });
                } else {
                    existing.unreadCount += (item.unreadCount || 0);
                    if (!existing.allMailboxIds.includes(item.userMailboxId)) {
                        existing.allMailboxIds.push(item.userMailboxId);
                    }
                }
            });

            const finalItems = Array.from(groupedMap.values()).sort((a, b) => 
                new Date(b.lastMessageAt) - new Date(a.lastMessageAt)
            );

            setMailboxItems(finalItems);
        } catch (err) {
            console.error("Error fetching mailbox", err);
            toast.error("Failed to load messages");
        } finally {
            setIsLoadingMailbox(false);
        }
    }, [currentUserId, resolvedOrgId, token, authAxios]);

    const fetchConversationMessages = async (conversationId) => {
        if (!currentUserId || !token || !conversationId) return;
        setIsLoadingMessages(true);
        try {
            const res = await authAxios.get(`${configData.INBOX_CONVERSATION}/${conversationId}/messages`, {
                params: { userId: String(currentUserId), size: 50, orgId: String(resolvedOrgId) }
            });
            setSelectedConversationMessages(res.data.items || []);
        } catch (err) {
            console.error("Error fetching messages", err);
            toast.error(`Error loading messages (UserId: ${currentUserId}, ConvId: ${conversationId}, OrgId: ${resolvedOrgId}). Server said: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsLoadingMessages(false);
            // Sync counts immediately after auto-read
            refreshUnreadCount();
        }
    };

    const updateStatus = async (conversationId, status, mailboxIds = []) => {
        if (!currentUserId || !token || !conversationId) return;
        try {
            await authAxios.patch(`${configData.INBOX_CONVERSATION}/${conversationId}/status`, null, {
                params: { userId: String(currentUserId), status, orgId: String(resolvedOrgId) }
            });
            if (status === 'READ') {
                setMailboxItems(prev => prev.map(item => 
                    item.conversationId === conversationId ? { ...item, unreadCount: 0 } : item
                ));
                refreshUnreadCount();
            }
        } catch (err) {
            console.error("Error updating status", err);
            toast.error(`Error updating read status: ${err.response?.data?.message || err.message}`);
        }
    };

    useEffect(() => {
        fetchMailbox();
        
        // Background polling for new messages
        const interval = setInterval(() => {
            fetchMailbox();
        }, 15000); // 15 seconds

        return () => clearInterval(interval);
    }, [fetchMailbox]);

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [selectedConversationMessages]);

    // Auto-read selected message if new replies arrive while viewing
    useEffect(() => {
        if (selectedMessage) {
            const currentItem = mailboxItems.find(item => item.conversationId === selectedMessage.conversationId);
            if (currentItem && currentItem.unreadCount > 0) {
                // Mark as read on backend
                updateStatus(selectedMessage.conversationId, 'READ', currentItem.allMailboxIds);
                // Refresh messages to show new ones
                fetchConversationMessages(selectedMessage.conversationId);
                // Update local selected state
                setSelectedMessage(prev => ({ ...prev, unreadCount: 0 }));
                // Force update the item in our current list
                setMailboxItems(prev => prev.map(m => 
                    m.conversationId === selectedMessage.conversationId ? { ...m, unreadCount: 0 } : m
                ));
            }
        }
    }, [mailboxItems, selectedMessage?.conversationId]);

    const handleSelectMessage = (item) => {
        setIsComposeOpen(false);
        setSelectedMessage(item);
        // fetchConversationMessages now triggers a backend auto-read
        fetchConversationMessages(item.conversationId);
        
        // Mark as read in the local list for instant feedback
        setMailboxItems(prev => prev.map(msg =>
            msg.conversationId === item.conversationId ? { ...msg, unreadCount: 0 } : msg
        ));
    };

    const handleSendReply = async () => {
        if (!replyBody.trim() || !selectedMessage) return;
        
        // If messages are still loading, wait or alert
        if (isLoadingMessages) {
            toast.info("Please wait for conversation to load...");
            return;
        }

        setIsSendingReply(true);
        try {
            // Find the other participant's ID from the messages. 
            // In a broadcast, this is the senderId of the original message that isn't the current user.
            let otherParticipantId = selectedConversationMessages.find(m => m.senderId !== currentUserId)?.senderId;

            // Fallback to targetId if it's a direct message (where targetId is the partner)
            if (!otherParticipantId && selectedMessage.targetType === 'INDIVIDUAL' && selectedMessage.targetId !== currentUserId) {
                otherParticipantId = selectedMessage.targetId;
            }

            if (!otherParticipantId) {
                toast.error("Could not determine recipient. Please refresh or wait for the conversation to load.");
                return;
            }

            let finalReplyBody = replyBody.trim();
            const activeAtts = replyAttachments.filter(a => a.status === 'ACTIVE' && a.downloadUrl);
            if (activeAtts.length > 0) {
                const links = activeAtts.map(a => `📎 Document: [${a.filename}](${a.downloadUrl})`).join('\n');
                finalReplyBody += `\n\nAttachments:\n${links}`;
            }
            const attachmentUrls = activeAtts.map(a => a.downloadUrl);

            await authAxios.post(configData.INBOX_SEND, {
                subject: selectedMessage.subject,
                body: finalReplyBody,
                conversationId: selectedMessage.conversationId,
                orgId: resolvedOrgId,
                targetType: 'INDIVIDUAL',
                targetId: otherParticipantId,
                attachmentUrls: attachmentUrls.length > 0 ? attachmentUrls : undefined
            }, {
                params: { userId: currentUserId }
            });

            setReplyBody('');
            setReplyAttachments([]);
            toast.success("Reply sent!");
            fetchConversationMessages(selectedMessage.conversationId);
            fetchMailbox();
        } catch (err) {
            console.error("Error sending reply", err);
            toast.error("Failed to send reply");
        } finally {
            setIsSendingReply(false);
        }
    };

    const filteredMessages = mailboxItems.filter(m => 
        m.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.participantSummary.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="student-inbox-container">
            <div className="inbox-header">
                <h2><MailIcon /> My Messages</h2>
                <div className="header-actions">
                    <button className="student-compose-btn" onClick={() => {
                        setIsComposeOpen(true);
                        setSelectedMessage(null);
                    }}>
                        <EditIcon fontSize="small" style={{ marginRight: '5px' }} />
                        Compose
                    </button>
                    <div className="search-bar">
                        <SearchIcon fontSize="small" />
                        <input 
                            type="text" 
                            placeholder="Search in messages..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <IconButton onClick={fetchMailbox} size="small" className="refresh-btn">
                        <RefreshIcon />
                    </IconButton>
                </div>
            </div>

            <div className="inbox-content">
                {/* List Pane */}
                <div className="message-list-side">
                    {isLoadingMailbox && mailboxItems.length === 0 ? (
                        <div className="loading-center"><CircularProgress size={24} /></div>
                    ) : filteredMessages.length === 0 ? (
                        <div className="empty-state">No messages found</div>
                    ) : (
                        filteredMessages.map(item => (
                            <div 
                                key={item.conversationId} 
                                className={`message-item ${selectedMessage?.conversationId === item.conversationId ? 'active' : ''} ${item.unreadCount > 0 ? 'unread' : ''}`}
                                onClick={() => handleSelectMessage(item)}
                            >
                                <div className="item-avatar">
                                    {item.participantSummary[0]}
                                </div>
                                <div className="item-info">
                                    <div className="item-top">
                                        <span className="sender-name">{item.participantSummary}</span>
                                        <span className="msg-time">{new Date(item.lastMessageAt).toLocaleDateString()}</span>
                                    </div>
                                    <div className="msg-subject">{item.subject}</div>
                                    <div className="msg-preview">
                                        {item.lastMessageSnippet || item.lastMessagePreview || (item.lastMessageBody ? item.lastMessageBody.substring(0, 80) : "No preview")}
                                    </div>
                                </div>
                                {item.unreadCount > 0 && <div className="unread-dot" />}
                            </div>
                        ))
                    )}
                </div>

                {/* Thread Pane */}
                <div className="message-thread-side">
                    {selectedMessage ? (
                        <>
                            <div className="thread-header">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <h3>{selectedMessage.subject}</h3>
                                        <p>Conversation with {selectedMessage.participantSummary}</p>
                                    </div>
                                    <IconButton size="small" onClick={() => setSelectedMessage(null)} style={{ color: '#64748b' }}>
                                        <CloseIcon />
                                    </IconButton>
                                </div>
                            </div>
                            
                            <div className="thread-body">
                                {isLoadingMessages ? (
                                    <div className="loading-center"><CircularProgress size={24} /></div>
                                ) : (
                                    selectedConversationMessages.map(msg => (
                                        <div key={msg.messageId} className={`chat-bubble-wrapper ${msg.senderId === currentUserId ? 'sent' : 'received'}`}>
                                            <div className="chat-bubble">
                                                <div className="msg-text">
                                                     {renderMessageBody(msg.body)}
                                                </div>
                                                <div className="msg-meta">
                                                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={threadEndRef} />
                            </div>

                            <div className="thread-footer" style={{ flexDirection: 'column', gap: '8px' }}>
                                {replyAttachments.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', width: '100%' }}>
                                        {replyAttachments.map((f) => (
                                            <div key={f.id || f.fileId} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                                <span>📎 {f.filename} {f.status === 'UPLOADING' && '(uploading...)'}</span>
                                                <IconButton size="small" onClick={() => removeAttachment(f.fileId || f.id, 'reply')}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                                    <input 
                                        type="file" 
                                        ref={replyAttachmentRef} 
                                        onChange={(e) => handleAttachmentSelect(e, 'reply')} 
                                        style={{ display: 'none' }} 
                                    />
                                    <IconButton 
                                        size="small" 
                                        onClick={() => replyAttachmentRef.current?.click()}
                                        disabled={isUploadingAttachment}
                                        title="Attach document"
                                        style={{ color: '#014486' }}
                                    >
                                        {isUploadingAttachment ? <CircularProgress size={16} /> : <AttachFileIcon />}
                                    </IconButton>
                                    <textarea 
                                        placeholder="Type your reply..."
                                        value={replyBody}
                                        onChange={(e) => setReplyBody(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendReply();
                                            }
                                        }}
                                        style={{ flex: 1 }}
                                    />
                                    <button 
                                        className="send-reply-btn"
                                        disabled={(!replyBody.trim() && replyAttachments.length === 0) || isSendingReply}
                                        onClick={handleSendReply}
                                    >
                                        {isSendingReply ? <CircularProgress size={16} /> : <SendIcon />}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="no-message-selected">
                            <ChatIcon />
                            <p>Select a message to read</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Compose Modal */}
            {isComposeOpen && (
                <>
                    <div className="compose-backdrop" onClick={() => setIsComposeOpen(false)} />
                    <div className="compose-overlay-premium">
                        <div className="compose-header">
                            <div className="header-text" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', fontWeight: 'bold' }}>
                                <EditIcon fontSize="small" />
                                <span>New Message</span>
                            </div>
                            <IconButton size="small" onClick={() => setIsComposeOpen(false)} style={{ color: 'white' }}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <div className="compose-body">
                            <div className="compose-field">
                                <label>To:</label>
                                <Select 
                                    options={facultyAdminOptions}
                                    value={composeRecipient}
                                    onChange={setComposeRecipient}
                                    placeholder="Select Faculty or Admin..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="compose-field">
                                <label>Subject:</label>
                                <input 
                                    type="text" 
                                    value={composeSubject}
                                    onChange={(e) => setComposeSubject(e.target.value)}
                                    placeholder="Enter subject..."
                                    className="compose-input"
                                />
                            </div>
                            <div className="compose-field compose-field-body">
                                <label>Message:</label>
                                <textarea 
                                    value={composeBody}
                                    onChange={(e) => setComposeBody(e.target.value)}
                                    placeholder="Type your message here..."
                                    className="compose-textarea"
                                />
                            </div>
                            <div className="compose-field compose-field-attachments" style={{ marginTop: '12px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>Attachments:</span>
                                    <input 
                                        type="file" 
                                        ref={composeAttachmentRef} 
                                        onChange={(e) => handleAttachmentSelect(e, 'compose')} 
                                        style={{ display: 'none' }} 
                                    />
                                    <button 
                                        type="button"
                                        onClick={() => composeAttachmentRef.current?.click()}
                                        disabled={isUploadingAttachment}
                                        style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}
                                    >
                                        {isUploadingAttachment ? <CircularProgress size={14} /> : <AttachFileIcon fontSize="small" />}
                                        <span>Attach File</span>
                                    </button>
                                </label>
                                {composeAttachments.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                        {composeAttachments.map((f) => (
                                            <div key={f.id || f.fileId} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#e2e8f0', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                                <span>📎 {f.filename} {f.status === 'UPLOADING' && '(uploading...)'}</span>
                                                <IconButton size="small" onClick={() => removeAttachment(f.fileId || f.id, 'compose')}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="compose-footer">
                            <button 
                                className="send-new-btn"
                                onClick={handleSendNewMessage}
                                disabled={isSendingNewMessage}
                            >
                                {isSendingNewMessage ? <CircularProgress size={16} style={{ color: 'white' }} /> : 'Send Message'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default StudentInbox;
